import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TermsAndConditions } from './TermsAndConditions';
import { PrivacyPolicy } from './PrivacyPolicy';
import { EmailRegistrationFormProgressive } from './EmailRegistrationFormProgressive';
import { getFullVersion } from '../lib/version';
import PublicLanguageSelector from './PublicLanguageSelector';
import { API_BASE_URL } from '../constants';
import { createServerSession } from '../lib/serverSession';
import { getDeviceFingerprint } from '../lib/deviceFingerprint';
import { validateStrictEmail, FIELD_LIMITS } from '../lib/userInputValidation';
import { lockoutMessage } from '../lib/loginLockoutCountdown';


interface UnifiedAuthPageProps {
  onAuthSuccess: (user: any) => void;
  onBack: () => void;
  userType: 'shareholder' | 'affiliate';
}

// v4.8.87 — Turn a browser-level network failure (thrown by fetch BEFORE any
// HTTP response, e.g. "TypeError: Failed to fetch") into actionable guidance.
// This condition is common inside in-app/social webviews (Facebook, Instagram,
// Messenger) which frequently block fetch POST requests. Real API errors (which
// carry a server-provided message) are passed through unchanged.
const describeFetchError = (err: any, fallback: string): string => {
  const raw = String(err?.message || '');
  const isNetworkFailure =
    err instanceof TypeError ||
    /failed to fetch/i.test(raw) ||
    /networkerror/i.test(raw) ||
    /load failed/i.test(raw);
  if (isNetworkFailure) {
    return "Couldn't reach the server. If you opened this link from inside another app (Facebook, Instagram, WhatsApp), tap the ⋮ / share icon and choose \"Open in browser\", then use Chrome or Safari and try again. Otherwise check your internet connection and retry.";
  }
  return raw || fallback;
};

export const UnifiedAuthPageClean: React.FC<UnifiedAuthPageProps> = ({
  onAuthSuccess,
  onBack,
  userType = 'shareholder'
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'web-login' | 'web-register'>('web-login');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lockoutUntilMs, setLockoutUntilMs] = useState<number | null>(null);
  const [lockoutNowMs, setLockoutNowMs] = useState(() => Date.now());
  const [success, setSuccess] = useState<string | null>(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetPin, setResetPin] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetStep, setResetStep] = useState<'email' | 'pin' | 'password'>('email');
  const [resetToken, setResetToken] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  // v4.3.91 — TOTP challenge state (populated when /api/initiate-login
  // returns challengeType === 'totp' for an MFA-enabled account).
  const [totpChallenge, setTotpChallenge] = useState<{ challengeId: string; expiresAt?: string } | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [totpTrustDevice, setTotpTrustDevice] = useState(false);
  const [totpUseBackup, setTotpUseBackup] = useState(false);
  const [totpVerifying, setTotpVerifying] = useState(false);
  const [totpError, setTotpError] = useState<string | null>(null);
  const [totpSetupChallenge, setTotpSetupChallenge] = useState<{
    challengeId: string;
    expiresAt?: string;
    qrCodeDataUrl?: string | null;
    secret?: string;
    backupCodes?: string[];
  } | null>(null);

  // v4.4.1 — Geo-OTP challenge state (populated when /api/initiate-login
  // returns challengeType === 'login_geo' because the request IP country
  // does not match the user's country_of_residence). The 6-digit OTP is
  // delivered to the registered email by lib/loginSecurity.js.
  const [geoChallenge, setGeoChallenge] = useState<{
    challengeId: string;
    maskedEmail?: string;
    ipCountry?: string;
    expectedCountry?: string;
    expiresAt?: string;
  } | null>(null);
  const [geoOtp, setGeoOtp] = useState('');

  // v4.7.36 (audit H2) — Forced-password-change challenge state. Populated
  // when /api/initiate-login returns challengeType === 'password_change_required'
  // because an admin issued a temporary password (users.must_change_password=true).
  // The user is held out of the dashboard until they submit a new strong
  // password to /api/auth/complete-forced-password-change.
  const [forcedPwdChallenge, setForcedPwdChallenge] = useState<{
    changeToken: string;
    expiresAt?: string;
    message?: string;
  } | null>(null);
  const [forcedPwdNew, setForcedPwdNew] = useState('');
  const [forcedPwdConfirm, setForcedPwdConfirm] = useState('');
  const [forcedPwdSubmitting, setForcedPwdSubmitting] = useState(false);
  const [forcedPwdError, setForcedPwdError] = useState<string | null>(null);
  const [forcedPwdShowNew, setForcedPwdShowNew] = useState(false);
  const [geoVerifying, setGeoVerifying] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  // v4.7.66 — Master-admin TOTP modal removed from the public login form.
  // Impersonation is now an authenticated admin action triggered from the
  // AdminDashboard "User Assistance" tab (components/admin/UserAssistance.tsx),
  // which calls /api/auth/master-admin-login with the admin's TOTP. This
  // eliminates the broken client-side `isMasterPassword()` pre-check (Vite
  // does not expose process.env to the browser) and keeps high-privilege
  // impersonation behind an existing verified admin session + MFA.

  const lockSecondsLeft = lockoutUntilMs == null
    ? null
    : Math.max(0, Math.ceil((lockoutUntilMs - lockoutNowMs) / 1000));

  useEffect(() => {
    if (lockoutUntilMs == null) return;
    setLockoutNowMs(Date.now());
    const id = window.setInterval(() => setLockoutNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [lockoutUntilMs]);

  const handleWebLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (!loginForm.email || !loginForm.password) {
        setError('Please enter both email and password');
        setIsLoading(false);
        return;
      }

      // Clear existing session data before new login
      localStorage.removeItem('aureus_test_user');
      localStorage.removeItem('aureus_telegram_user');
      localStorage.removeItem('aureus_user');
      localStorage.removeItem('aureus_session');
      localStorage.removeItem('aureus_auth_timestamp');
      localStorage.removeItem('aureus_user_type');
      localStorage.removeItem('aureus_master_view_session');

      // v4.7.66 — Master-password detection removed from this form. See
      // AdminDashboard > User Assistance for the authenticated impersonation
      // flow. Login continues straight to /api/initiate-login below.

      const deviceFingerprint = getDeviceFingerprint();
      const response = await fetch(`${API_BASE_URL}/api/initiate-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emailOrUsername: loginForm.email,
          password: loginForm.password,
          deviceFingerprint
        })
      });

      const result = await response.json();

      // v4.4.1 — Geo-OTP challenge: password verified but the request IP
      // resolved to a country other than the user's country_of_residence.
      // Open the geo modal so the user can enter the 6-digit code that
      // /api/initiate-login already emailed via lib/loginSecurity.js.
      if (result?.challengeRequired && result?.challengeType === 'login_geo' && result?.challengeId) {
        setGeoChallenge({
          challengeId: result.challengeId,
          maskedEmail: result.maskedEmail,
          ipCountry: result.ipCountry,
          expectedCountry: result.expectedCountry,
          expiresAt: result.expiresAt
        });
        setGeoOtp('');
        setGeoError(null);
        setIsLoading(false);
        return;
      }

      // v4.3.91 — TOTP MFA challenge: hold the password success and prompt
      // for the authenticator-app code (or backup code).
      if (result?.challengeRequired && result?.challengeType === 'totp' && result?.challengeId) {
        setTotpChallenge({ challengeId: result.challengeId, expiresAt: result.expiresAt });
        setTotpCode('');
        setTotpUseBackup(false);
        setTotpTrustDevice(false);
        setTotpError(null);
        setIsLoading(false);
        return;
      }

      if (result?.challengeRequired && result?.challengeType === 'totp_setup' && result?.challengeId) {
        setTotpSetupChallenge({
          challengeId: result.challengeId,
          expiresAt: result.expiresAt,
          qrCodeDataUrl: result.qrCodeDataUrl || null,
          secret: result.secret || '',
          backupCodes: Array.isArray(result.backupCodes) ? result.backupCodes : []
        });
        setTotpCode('');
        setTotpTrustDevice(false);
        setTotpError(null);
        setIsLoading(false);
        return;
      }

      // v4.7.36 (H2) — Forced password change challenge: an admin issued a
      // temporary password and users.must_change_password=true. The backend
      // verified the temporary credentials but refuses to mint a session
      // until a new strong password is set. Show the forced-change modal.
      if (result?.challengeRequired && result?.challengeType === 'password_change_required' && result?.changeToken) {
        setForcedPwdChallenge({
          changeToken: result.changeToken,
          expiresAt: result.expiresAt,
          message: result.message
        });
        setForcedPwdNew('');
        setForcedPwdConfirm('');
        setForcedPwdError(null);
        setForcedPwdShowNew(false);
        setIsLoading(false);
        return;
      }

      if (!result.success) {
        const retryAfterSeconds = Number(result.retryAfterSeconds);
        if (response.status === 423 && Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
          setLockoutUntilMs(Date.now() + Math.ceil(retryAfterSeconds) * 1000);
          setLockoutNowMs(Date.now());
          setError(null);
        } else {
          setLockoutUntilMs(null);
          setError(result.error || result.message || 'Login failed');
        }
        setIsLoading(false);
        return;
      }
      setLockoutUntilMs(null);

      await completeLogin(result);
    } catch (err: any) {
      console.error('❌ Web login exception:', err);
      setError(describeFetchError(err, 'Login failed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Shared post-credential success path: builds the structuredUser, persists
  // session state, and notifies the parent. Used by the direct-login path and
  // by the TOTP verification path (api/auth/mfa/verify-login.js returns the
  // same user shape + loginToken).
  const completeLogin = async (result: any) => {
    const user = result.user;
    const numericId = typeof user.id === 'number' ? user.id : parseInt(user.id);

    const structuredUser = {
      id: numericId,
      email: user.email,
      username: user.username,
      account_type: 'web',
      user_type: user.account_type || userType || 'shareholder',
      database_user: {
        id: numericId,
        email: user.email,
        username: user.username,
        full_name: user.full_name,
        phone: user.phone,
        country_of_residence: user.country_of_residence,
        profile_image_url: user.profile_image_url,
        user_type: user.account_type || userType || 'shareholder',
        is_active: user.is_active,
        auth_user_id: user.auth_user_id
      },
      user_metadata: {
        user_id: numericId,
        email: user.email,
        username: user.username,
        full_name: user.full_name,
        profile_image_url: user.profile_image_url,
        user_type: user.account_type || userType || 'shareholder',
        account_type: 'web'
      }
    };

    localStorage.setItem('aureus_user', JSON.stringify(structuredUser));
    localStorage.setItem('aureus_user_type', structuredUser.user_type);
    localStorage.setItem('aureus_auth_timestamp', Date.now().toString());
    localStorage.setItem('aureus_session', JSON.stringify({
      user_id: numericId,
      email: user.email,
      account_type: 'web',
      user_type: structuredUser.user_type,
      logged_in_at: new Date().toISOString()
    }));

    await createServerSession(numericId, user.email, 'email', undefined, result.loginToken);
    onAuthSuccess(structuredUser);
  };

  // Submit the TOTP / backup code to api/auth/mfa/verify-login. On success
  // the same user payload + loginToken is returned and we run completeLogin.
  const handleTotpSubmit = async () => {
    if (!totpChallenge?.challengeId) return;
    const code = totpCode.trim();
    const expectLen = totpUseBackup ? 8 : 6;
    if (!new RegExp(`^\\d{${expectLen}}$`).test(code)) {
      setTotpError(`Enter a ${expectLen}-digit ${totpUseBackup ? 'backup' : 'authenticator'} code.`);
      return;
    }
    setTotpVerifying(true);
    setTotpError(null);
    try {
      const fingerprint = getDeviceFingerprint();
      const r = await fetch(`${API_BASE_URL}/api/auth/mfa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challengeId: totpChallenge.challengeId,
          token: code,
          fingerprint,
          trustDevice: totpTrustDevice
        })
      });
      const j = await r.json();
      if (!j?.success) {
        setTotpError(j?.error || 'Invalid code. Please try again.');
        setTotpVerifying(false);
        return;
      }
      // Success — clear challenge state and complete the login.
      setTotpChallenge(null);
      setTotpCode('');
      setTotpUseBackup(false);
      setTotpTrustDevice(false);
      await completeLogin(j);
    } catch (e: any) {
      setTotpError(e?.message || 'Network error.');
    } finally {
      setTotpVerifying(false);
    }
  };

  const handleTotpSetupSubmit = async () => {
    if (!totpSetupChallenge?.challengeId) return;
    const code = totpCode.trim();
    if (!/^\d{6}$/.test(code)) {
      setTotpError('Enter the 6-digit authenticator code.');
      return;
    }
    setTotpVerifying(true);
    setTotpError(null);
    try {
      const fingerprint = getDeviceFingerprint();
      const r = await fetch(`${API_BASE_URL}/api/auth/mfa/verify-setup-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challengeId: totpSetupChallenge.challengeId,
          token: code,
          fingerprint,
          trustDevice: totpTrustDevice
        })
      });
      const j = await r.json();
      if (!j?.success) {
        setTotpError(j?.error || 'Invalid code. Please try again.');
        setTotpVerifying(false);
        return;
      }
      setTotpSetupChallenge(null);
      setTotpCode('');
      setTotpTrustDevice(false);
      await completeLogin(j);
    } catch (e: any) {
      setTotpError(e?.message || 'Network error.');
    } finally {
      setTotpVerifying(false);
    }
  };

  // v4.7.66 — handleMasterTotpSubmit removed; master-password impersonation
  // is handled by AdminDashboard > User Assistance.

  // v4.4.1 — Submit the 6-digit geo OTP to /api/verify-login-challenge.
  // On success the same user payload + loginToken contract as initiate-login
  // is returned and we run completeLogin. If the user has TOTP MFA enabled
  // and the device is not trusted, the backend escalates by returning a
  // fresh totp challenge — we hand off to the existing TOTP modal in that case.
  const handleGeoSubmit = async () => {
    if (!geoChallenge?.challengeId) return;
    const code = geoOtp.trim();
    if (!/^\d{6}$/.test(code)) {
      setGeoError('Enter the 6-digit code from your email.');
      return;
    }
    setGeoVerifying(true);
    setGeoError(null);
    try {
      const fingerprint = getDeviceFingerprint();
      const r = await fetch(`${API_BASE_URL}/api/verify-login-challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challengeId: geoChallenge.challengeId,
          otp: code,
          deviceFingerprint: fingerprint
        })
      });
      const j = await r.json();

      // Backend may escalate to TOTP if the user has authenticator-app MFA
      // enabled and this device is not in trusted_devices.
      if (j?.challengeRequired && j?.challengeType === 'totp' && j?.challengeId) {
        setGeoChallenge(null);
        setGeoOtp('');
        setTotpChallenge({ challengeId: j.challengeId, expiresAt: j.expiresAt });
        setTotpCode('');
        setTotpUseBackup(false);
        setTotpTrustDevice(false);
        setTotpError(null);
        return;
      }

      if (!j?.success) {
        setGeoError(j?.error || 'Invalid verification code. Please try again.');
        return;
      }

      // Success — clear challenge state and complete the login.
      setGeoChallenge(null);
      setGeoOtp('');
      await completeLogin(j);
    } catch (e: any) {
      setGeoError(e?.message || 'Network error.');
    } finally {
      setGeoVerifying(false);
    }
  };

  // v4.7.36 (audit H2) — Submit the new password to /api/auth/complete-forced-password-change.
  // On success the endpoint returns the same { user, loginToken } shape as
  // /api/initiate-login so we hand off directly to completeLogin().
  const handleForcedPwdSubmit = async () => {
    if (!forcedPwdChallenge?.changeToken) return;
    const np = forcedPwdNew;
    const cf = forcedPwdConfirm;
    // Mirror the server-side strength rules so users get instant feedback
    // instead of a round-trip to discover their password is too weak.
    if (np.length < 8) {
      setForcedPwdError('Password must be at least 8 characters.');
      return;
    }
    if (!/[a-z]/.test(np) || !/[A-Z]/.test(np) || !/[0-9]/.test(np) || !/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(np)) {
      setForcedPwdError('Password must contain uppercase, lowercase, number, and special character.');
      return;
    }
    if (np !== cf) {
      setForcedPwdError('Passwords do not match.');
      return;
    }
    setForcedPwdSubmitting(true);
    setForcedPwdError(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/auth/complete-forced-password-change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          changeToken: forcedPwdChallenge.changeToken,
          newPassword: np
        })
      });
      const j = await r.json();
      if (!j?.success) {
        const detail = Array.isArray(j?.details) && j.details.length > 0 ? ` (${j.details.join('; ')})` : '';
        setForcedPwdError((j?.error || 'Failed to set new password.') + detail);
        return;
      }
      // Success — clear challenge state and complete the login.
      setForcedPwdChallenge(null);
      setForcedPwdNew('');
      setForcedPwdConfirm('');
      await completeLogin(j);
    } catch (e: any) {
      setForcedPwdError(e?.message || 'Network error.');
    } finally {
      setForcedPwdSubmitting(false);
    }
  };

  const handleLoginInputChange = (field: string, value: string) => {
    setLoginForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSendResetPin = async () => {
    if (!forgotPasswordEmail || !forgotPasswordEmail.trim()) {
      setError('Please enter your email address');
      return;
    }

    const emailCheck = validateStrictEmail(forgotPasswordEmail);
    if (!emailCheck.valid) {
      setError(emailCheck.error!);
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-reset-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset PIN');
      }

      setSuccess(data.message || 'Reset PIN sent to your email address. Please check your inbox.');
      setResetStep('pin');
    } catch (err: any) {
      setError(describeFetchError(err, 'Failed to send reset PIN. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyPin = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/verify-reset-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail.trim(), pin: resetPin.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to verify PIN');
      }

      // V5: Store the cryptographic reset token from the verify response
      if (data.resetToken) {
        setResetToken(data.resetToken);
      }
      setSuccess(data.message || 'PIN verified successfully. Please enter your new password.');
      setResetStep('password');
    } catch (err: any) {
      setError(describeFetchError(err, 'Invalid or expired PIN. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // V5: Include the cryptographic reset token
      const response = await fetch(`${API_BASE_URL}/api/password-reset`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail.trim(), newPassword: newPassword, resetToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to reset password');
      }

      setSuccess(data.message || 'Password reset successfully! You can now log in with your new password.');

      setTimeout(() => {
        setShowForgotPassword(false);
        setResetStep('email');
        setForgotPasswordEmail('');
        setResetPin('');
        setNewPassword('');
        setConfirmNewPassword('');
        setResetToken('');
      }, 2000);
    } catch (err: any) {
      setError(describeFetchError(err, 'Failed to reset password. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };








  return (
    <div className="modern-website premium-auth-page">
      {/* Header */}
      <header className="header premium-header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img
                src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo-s.png"
                alt="Aureus Alliance Holdings"
                className="logo-image"
              />
            </div>
            <nav className="nav-menu">
              <div className="nav-actions">
                <PublicLanguageSelector />
                <button className="nav-item premium-nav-item" onClick={onBack}>
                  Home
                </button>
                <button className="btn btn-primary premium-btn">
                  Purchase Shares
                </button>
              </div>
            </nav>
          </div>
        </div>
      </header>



      {/* Main Content */}
      <main className="content-section premium-content">
        <div className="container">
          <div className="auth-container premium-auth-container">


            {/* Tab Navigation */}
            <div className="tab-navigation premium-tab-navigation">
              <button
                onClick={() => setActiveTab('web-login')}
                className={`tab-button premium-tab-button ${activeTab === 'web-login' ? 'active' : ''}`}
              >
                <span className="tab-icon">🔐</span>
                {t('auth.tab.webLogin')}
              </button>
              <button
                onClick={() => setActiveTab('web-register')}
                className={`tab-button premium-tab-button ${activeTab === 'web-register' ? 'active' : ''}`}
              >
                <span className="tab-icon">✨</span>
                {userType === 'shareholder' ? t('auth.tab.createAccount') : t('auth.tab.register')}
              </button>
            </div>

            {/* Error/Success Messages - Hide login errors since they show under password field */}
            {error && activeTab !== 'web-login' && (
              <div className="alert alert-error">
                <div className="alert-content">
                  <span className="alert-icon">⚠️</span>
                  <p>{error}</p>
                </div>
              </div>
            )}

            {success && (
              <div className="alert alert-success">
                <div className="alert-content">
                  <span className="alert-icon">✅</span>
                  <p>{success}</p>
                </div>
              </div>
            )}

            {/* Content Cards */}
            <div className="auth-content premium-auth-content">

              {activeTab === 'web-login' && (
                <div className="card premium-card">
                      <div className="card-header premium-card-header">
                        <h2 className="card-title premium-card-title">{t('auth.login.heading')}</h2>
                        <p className="card-subtitle premium-card-subtitle">
                          {t('auth.login.subtitle')}
                        </p>
                      </div>
                      <div className="card-content premium-card-content">
                        <form onSubmit={handleWebLogin} className="form">
                      <div className="form-group">
                        <label className="form-label">{t('auth.login.emailOrUsername')}</label>
                        <input
                          type="text"
                          required
                          maxLength={FIELD_LIMITS.email.max}
                          value={loginForm.email}
                          onChange={(e) => handleLoginInputChange('email', e.target.value)}
                          className="form-input"
                          placeholder={t('auth.login.emailOrUsernamePlaceholder')}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('auth.login.password')}</label>
                        <div className="password-input-container">
                          <input
                            type={showPassword ? "text" : "password"}
                            required
                            value={loginForm.password}
                            onChange={(e) => handleLoginInputChange('password', e.target.value)}
                            className="form-input password-input"
                            placeholder={t('auth.login.passwordPlaceholder')}
                          />
                          <button
                            type="button"
                            className="password-toggle-btn"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                          >
                            {showPassword ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                <line x1="1" y1="1" x2="23" y2="23"/>
                              </svg>
                            ) : (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                              </svg>
                            )}
                          </button>
                        </div>
                        {/* Password-specific error message */}
                        {(error || lockSecondsLeft != null) && activeTab === 'web-login' && (
                          <div className="field-error-message">
                            <span className="error-icon">⚠️</span>
                            <span>{lockSecondsLeft != null ? lockoutMessage(lockSecondsLeft) : error}</span>
                          </div>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="btn btn-primary btn-lg"
                      >
                        {isLoading ? t('auth.login.signingIn') : t('auth.login.signInButton')}
                      </button>

                      <div className="forgot-password-link">
                        <div className="flex justify-center items-center gap-4">
                          <button
                            type="button"
                            onClick={() => {
                              setResetStep('email');
                              setForgotPasswordEmail('');
                              setResetPin('');
                              setNewPassword('');
                              setConfirmNewPassword('');
                              setResetToken('');
                              setError(null);
                              setLockoutUntilMs(null);
                              setSuccess(null);
                              setShowForgotPassword(true);
                            }}
                            className="link-button"
                          >
                            {t('auth.login.forgotPassword')}
                          </button>
                        </div>
                      </div>


                    </form>
                      </div>
                </div>
              )}

              {activeTab === 'web-register' && (
                <div className="card premium-card">
                  <div className="card-header premium-card-header">
                    <h2 className="card-title premium-card-title">
                      {userType === 'shareholder' ? t('auth.register.heading.shareholder') : t('auth.register.heading.generic')}
                    </h2>
                    <p className="card-subtitle premium-card-subtitle">
                      {userType === 'shareholder'
                        ? t('auth.register.subtitle.shareholder')
                        : t('auth.register.subtitle.generic')
                      }
                    </p>
                  </div>
                  <div className="card-content premium-card-content">
                    <EmailRegistrationFormProgressive
                      onRegistrationSuccess={onAuthSuccess}
                      userType={userType}
                      onSwitchToLogin={() => setActiveTab('web-login')}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* v4.3.91 — TOTP Verification Modal (shown when initiate-login returns
          challengeType=totp). Submits to /api/auth/mfa/verify-login. */}
      {totpChallenge && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="totp-modal-title">
          <div className="modal-content" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h2 id="totp-modal-title">Two-Factor Authentication</h2>
              <button
                onClick={() => {
                  if (totpVerifying) return;
                  setTotpChallenge(null);
                  setTotpCode('');
                  setTotpError(null);
                  setTotpUseBackup(false);
                  setTotpTrustDevice(false);
                }}
                className="modal-close"
                aria-label="Close"
              >×</button>
            </div>
            <div className="modal-body" style={{ padding: 20 }}>
              <p style={{ color: '#ddd', marginBottom: 16, fontSize: 14 }}>
                {totpUseBackup
                  ? 'Enter one of your 8-digit backup codes. Each code can only be used once.'
                  : 'Enter the 6-digit code from your authenticator app (Google Authenticator, Authy, 1Password, etc.).'}
              </p>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={totpUseBackup ? 8 : 6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter' && !totpVerifying) handleTotpSubmit(); }}
                placeholder={totpUseBackup ? '8-digit backup code' : '6-digit code'}
                className="form-control pin-input"
                style={{ width: '100%', padding: 12, fontSize: 18, letterSpacing: 4, textAlign: 'center', marginBottom: 12 }}
                disabled={totpVerifying}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ddd', fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={totpTrustDevice}
                  onChange={(e) => setTotpTrustDevice(e.target.checked)}
                  disabled={totpVerifying}
                />
                Trust this device for 30 days
              </label>
              {totpError && (
                <div className="field-error-message" style={{ marginBottom: 12 }}>
                  <span className="error-icon">⚠️</span>
                  <span>{totpError}</span>
                </div>
              )}
              <button
                type="button"
                onClick={handleTotpSubmit}
                disabled={totpVerifying || totpCode.length !== (totpUseBackup ? 8 : 6)}
                className="btn btn-primary btn-lg"
                style={{ width: '100%', marginBottom: 8 }}
              >
                {totpVerifying ? 'Verifying…' : 'Verify & Sign In'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setTotpUseBackup(!totpUseBackup);
                  setTotpCode('');
                  setTotpError(null);
                }}
                disabled={totpVerifying}
                className="link-button"
                style={{ width: '100%', textAlign: 'center' }}
              >
                {totpUseBackup ? 'Use authenticator app code instead' : 'Use a backup code instead'}
              </button>
            </div>
          </div>
        </div>
      )}

      {totpSetupChallenge && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="totp-setup-modal-title">
          <div className="modal-content" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h2 id="totp-setup-modal-title">Set up authenticator</h2>
              <button
                onClick={() => {
                  if (totpVerifying) return;
                  setTotpSetupChallenge(null);
                  setTotpCode('');
                  setTotpError(null);
                  setTotpTrustDevice(false);
                }}
                className="modal-close"
                aria-label="Close"
              >×</button>
            </div>
            <div className="modal-body" style={{ padding: 20 }}>
              <p style={{ color: '#ddd', marginBottom: 12, fontSize: 14 }}>
                Scan this QR with Google Authenticator, Authy, or 1Password. Save the backup codes. Then enter the 6-digit code. You are not signed in until that code is confirmed.
              </p>
              {totpSetupChallenge.qrCodeDataUrl ? (
                <img
                  src={totpSetupChallenge.qrCodeDataUrl}
                  alt="Authenticator QR code"
                  style={{ display: 'block', width: 192, height: 192, margin: '0 auto 12px', background: '#fff', padding: 8 }}
                />
              ) : null}
              {totpSetupChallenge.secret ? (
                <p style={{ color: '#bbb', fontSize: 12, wordBreak: 'break-all', marginBottom: 12 }}>
                  Manual key: {totpSetupChallenge.secret}
                </p>
              ) : null}
              {totpSetupChallenge.backupCodes && totpSetupChallenge.backupCodes.length > 0 ? (
                <div style={{ background: 'rgba(255,255,255,0.06)', padding: 12, marginBottom: 12, fontSize: 13, color: '#eee' }}>
                  <strong>Backup codes (shown once)</strong>
                  <div style={{ marginTop: 8, fontFamily: 'monospace', letterSpacing: 1 }}>
                    {totpSetupChallenge.backupCodes.join('  ')}
                  </div>
                </div>
              ) : null}
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter' && !totpVerifying) handleTotpSetupSubmit(); }}
                placeholder="6-digit code"
                className="form-control pin-input"
                style={{ width: '100%', padding: 12, fontSize: 18, letterSpacing: 4, textAlign: 'center', marginBottom: 12 }}
                disabled={totpVerifying}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ddd', fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={totpTrustDevice}
                  onChange={(e) => setTotpTrustDevice(e.target.checked)}
                  disabled={totpVerifying}
                />
                Trust this device for 30 days
              </label>
              {totpError && (
                <div className="field-error-message" style={{ marginBottom: 12 }}>
                  <span className="error-icon">⚠️</span>
                  <span>{totpError}</span>
                </div>
              )}
              <button
                type="button"
                onClick={handleTotpSetupSubmit}
                disabled={totpVerifying || totpCode.length !== 6}
                className="btn btn-primary btn-lg"
                style={{ width: '100%' }}
              >
                {totpVerifying ? 'Verifying…' : 'Confirm authenticator and sign in'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* v4.7.66 — Master-admin TOTP modal removed. See AdminDashboard >
          User Assistance for the authenticated impersonation flow. */}

      {/* v4.4.2 — Geo-OTP Verification Modal (shown when initiate-login returns
          challengeType=login_geo because the request IP country differs from
          the user's country_of_residence). Submits to /api/verify-login-challenge. */}
      {geoChallenge && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="geo-modal-title">
          <div className="modal-content" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h2 id="geo-modal-title">Verify New Sign-in Location</h2>
              <button
                type="button"
                onClick={() => {
                  if (geoVerifying) return;
                  setGeoChallenge(null);
                  setGeoOtp('');
                  setGeoError(null);
                }}
                aria-label="Close"
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#fff',
                  fontSize: 22,
                  lineHeight: 1,
                  cursor: 'pointer',
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >×</button>
            </div>
            <div className="modal-body" style={{ padding: 20 }}>
              <p style={{ color: '#ddd', marginBottom: 12, fontSize: 14 }}>
                We detected a sign-in from <strong>{geoChallenge.ipCountry || 'an unfamiliar country'}</strong>
                {geoChallenge.expectedCountry ? <> but your account is registered in <strong>{geoChallenge.expectedCountry}</strong></> : null}.
              </p>
              <p style={{ color: '#ddd', marginBottom: 16, fontSize: 14 }}>
                A 6-digit verification code has been sent to{' '}
                <strong>{geoChallenge.maskedEmail || 'your registered email address'}</strong>.
                The code expires in 15 minutes.{' '}
                <span style={{ color: '#fbbf24' }}>If you don't see it, check your spam / junk folder.</span>
              </p>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
                value={geoOtp}
                onChange={(e) => setGeoOtp(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter' && !geoVerifying) handleGeoSubmit(); }}
                placeholder="6-digit code"
                className="form-control pin-input"
                style={{ width: '100%', padding: 12, fontSize: 18, letterSpacing: 4, textAlign: 'center', marginBottom: 12 }}
                disabled={geoVerifying}
              />
              {geoError && (
                <div className="field-error-message" style={{ marginBottom: 12 }}>
                  <span className="error-icon">⚠️</span>
                  <span>{geoError}</span>
                </div>
              )}
              <button
                type="button"
                onClick={handleGeoSubmit}
                disabled={geoVerifying || geoOtp.length !== 6}
                className="btn btn-primary btn-lg"
                style={{ width: '100%', marginBottom: 8 }}
              >
                {geoVerifying ? 'Verifying…' : 'Verify & Sign In'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (geoVerifying) return;
                  setGeoChallenge(null);
                  setGeoOtp('');
                  setGeoError(null);
                }}
                disabled={geoVerifying}
                style={{
                  width: '100%',
                  marginBottom: 8,
                  padding: 10,
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.25)',
                  color: '#ddd',
                  borderRadius: 6,
                  cursor: geoVerifying ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                Cancel
              </button>
              <p style={{ color: '#888', fontSize: 12, marginTop: 8, textAlign: 'center' }}>
                Didn't receive the email? Cancel and sign in again to request a new code.
                Each new request invalidates the previous code.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* v4.7.36 (audit H2) — Forced Password Change Modal (shown when
          initiate-login returns challengeType=password_change_required because
          an admin issued a temporary password). Submits to
          /api/auth/complete-forced-password-change. Modal cannot be dismissed
          without setting a new password — the user is intentionally trapped
          here until they rotate the temporary credential. */}
      {forcedPwdChallenge && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="forced-pwd-title">
          <div className="modal-content" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h2 id="forced-pwd-title">Set a New Password</h2>
            </div>
            <div className="modal-body" style={{ padding: 20 }}>
              <p style={{ color: '#ddd', fontSize: 14, marginBottom: 12 }}>
                {forcedPwdChallenge.message || 'Your password was reset by an administrator. Please set a new password to continue.'}
              </p>
              <p style={{ color: '#888', fontSize: 12, marginBottom: 16 }}>
                Must be at least 8 characters and include uppercase, lowercase, a number, and a special character.
              </p>
              <div className="password-input-container" style={{ marginBottom: 12 }}>
                <input
                  type={forcedPwdShowNew ? 'text' : 'password'}
                  placeholder="New password"
                  value={forcedPwdNew}
                  onChange={(e) => setForcedPwdNew(e.target.value)}
                  disabled={forcedPwdSubmitting}
                  className="form-input password-input"
                  style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #333', background: '#0A0A0A', color: '#FFF' }}
                />
                <button type="button" className="password-toggle-btn" onClick={() => setForcedPwdShowNew(!forcedPwdShowNew)}>
                  {forcedPwdShowNew ? '🙈' : '👁️'}
                </button>
              </div>
              <div className="password-input-container" style={{ marginBottom: 16 }}>
                <input
                  type={forcedPwdShowNew ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={forcedPwdConfirm}
                  onChange={(e) => setForcedPwdConfirm(e.target.value)}
                  disabled={forcedPwdSubmitting}
                  className="form-input password-input"
                  style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #333', background: '#0A0A0A', color: '#FFF' }}
                />
              </div>
              {forcedPwdError && (
                <div className="field-error-message" style={{ marginBottom: 12 }}>
                  <span className="error-icon">⚠️</span>
                  <span>{forcedPwdError}</span>
                </div>
              )}
              <button
                type="button"
                onClick={handleForcedPwdSubmit}
                disabled={forcedPwdSubmitting || !forcedPwdNew || !forcedPwdConfirm}
                className="btn btn-primary btn-lg"
                style={{ width: '100%' }}
              >
                {forcedPwdSubmitting ? 'Updating…' : 'Set New Password & Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terms and Conditions Modal */}
      {showTermsModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Terms and Conditions</h2>
              <button onClick={() => setShowTermsModal(false)} className="modal-close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <TermsAndConditions />
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Privacy Policy</h2>
              <button onClick={() => setShowPrivacyModal(false)} className="modal-close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <PrivacyPolicy />
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="modal-overlay" onClick={() => setShowForgotPassword(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <button onClick={() => setShowForgotPassword(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}
              {success && <div className="alert alert-success" style={{ marginBottom: '16px' }}>{success}</div>}

              {resetStep === 'email' && (
                <div>
                  <p style={{ color: '#aaa', fontSize: '14px', marginBottom: '16px' }}>Enter your email address and we'll send you a PIN to reset your password.</p>
                  <input type="email" placeholder="Email address" value={forgotPasswordEmail}
                    maxLength={FIELD_LIMITS.email.max}
                    onChange={e => setForgotPasswordEmail(e.target.value)}
                    className="form-input" style={{ width: '100%', marginBottom: '16px', padding: '12px', borderRadius: '8px', border: '1px solid #333', background: '#0A0A0A', color: '#FFF' }} />
                  <button onClick={handleSendResetPin} disabled={isLoading || !forgotPasswordEmail.trim()}
                    className="btn btn-primary" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: 'none', background: '#ffc107', color: '#000', fontWeight: 600, cursor: 'pointer' }}>
                    {isLoading ? 'Sending...' : 'Send Reset PIN'}
                  </button>
                </div>
              )}

              {resetStep === 'pin' && (
                <div>
                  <p style={{ color: '#aaa', fontSize: '14px', marginBottom: '16px' }}>Enter the PIN sent to your email.</p>
                  <input type="text" placeholder="Enter PIN" value={resetPin}
                    onChange={e => setResetPin(e.target.value)}
                    className="form-input pin-input" style={{ width: '100%', marginBottom: '16px', padding: '12px', borderRadius: '8px', border: '1px solid #333', background: '#0A0A0A', color: '#FFF', textAlign: 'center', fontSize: '20px', letterSpacing: '8px' }} />
                  <button onClick={handleVerifyPin} disabled={isLoading || !resetPin.trim()}
                    className="btn btn-primary" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: 'none', background: '#ffc107', color: '#000', fontWeight: 600, cursor: 'pointer' }}>
                    {isLoading ? 'Verifying...' : 'Verify PIN'}
                  </button>
                </div>
              )}

              {resetStep === 'password' && (
                <div>
                  <p style={{ color: '#aaa', fontSize: '14px', marginBottom: '16px' }}>Enter your new password.</p>
                  <div className="password-input-container" style={{ marginBottom: '12px' }}>
                    <input type={showNewPassword ? 'text' : 'password'} placeholder="New password" value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      className="form-input password-input" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #333', background: '#0A0A0A', color: '#FFF' }} />
                    <button type="button" className="password-toggle-btn" onClick={() => setShowNewPassword(!showNewPassword)}>
                      {showNewPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                  <div className="password-input-container" style={{ marginBottom: '16px' }}>
                    <input type={showConfirmNewPassword ? 'text' : 'password'} placeholder="Confirm new password" value={confirmNewPassword}
                      onChange={e => setConfirmNewPassword(e.target.value)}
                      className="form-input password-input" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #333', background: '#0A0A0A', color: '#FFF' }} />
                    <button type="button" className="password-toggle-btn" onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}>
                      {showConfirmNewPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                  {newPassword && confirmNewPassword && newPassword !== confirmNewPassword && (
                    <p style={{ color: '#ef4444', fontSize: '13px', marginBottom: '12px' }}>Passwords do not match</p>
                  )}
                  <button onClick={handleResetPassword}
                    disabled={isLoading || !newPassword || !confirmNewPassword || newPassword !== confirmNewPassword || newPassword.length < 6}
                    className="btn btn-primary" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: 'none', background: '#ffc107', color: '#000', fontWeight: 600, cursor: 'pointer' }}>
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Password Toggle Styles */}
      <style>{`
        .password-input-container {
          position: relative;
          display: flex;
          align-items: center;
        }

        .password-input {
          padding-right: 45px !important;
        }

        .password-toggle-btn {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .password-toggle-btn:hover {
          color: #ffc107;
          background-color: rgba(255, 193, 7, 0.1);
        }

        .password-toggle-btn:focus {
          outline: none;
          color: #ffc107;
          background-color: rgba(255, 193, 7, 0.1);
        }

        .password-toggle-btn svg {
          width: 18px;
          height: 18px;
        }

        .field-error-message {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 8px;
          padding: 8px 12px;
          background-color: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 6px;
          color: #fca5a5;
          font-size: 14px;
        }

        .error-icon {
          color: #ef4444;
          font-size: 16px;
        }

        .forgot-password-link {
          text-align: center;
          margin-top: 16px;
        }

        .link-button {
          background: none;
          border: none;
          color: #ffc107;
          cursor: pointer;
          font-size: 14px;
          text-decoration: underline;
          padding: 4px;
        }

        .link-button:hover {
          color: #FFD700;
        }

        /* v4.6.2 — TOTP/PIN inputs render on a white background; the
           surrounding modal inherits color:#fff from <body>, which made
           typed digits invisible (white-on-white). Force the foreground
           to a dark slate so users can see what they type, and override
           webkit autofill so the autofilled value matches the manual
           typed appearance instead of the browser's yellow highlight. */
        .pin-input {
          background-color: #ffffff !important;
          color: #111827 !important;
          caret-color: #111827;
          -webkit-text-fill-color: #111827;
        }

        .pin-input::placeholder {
          color: #6b7280;
          opacity: 1;
        }

        .pin-input:-webkit-autofill,
        .pin-input:-webkit-autofill:hover,
        .pin-input:-webkit-autofill:focus,
        .pin-input:-webkit-autofill:active {
          -webkit-text-fill-color: #111827 !important;
          -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
          caret-color: #111827;
          transition: background-color 9999s ease-in-out 0s;
        }

        .pin-input:focus {
          border-color: #ffc107 !important;
          box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.1) !important;
        }

        /* Mobile-specific styles for better touch interaction */
        .mobile-alert {
          font-size: 14px;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 16px;
        }

        .mobile-alert .alert-content {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
        }

        .mobile-alert .alert-dismiss {
          min-width: 24px;
          min-height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @media (max-width: 768px) {
          .btn {
            min-height: 48px !important;
            font-size: 16px !important;
            padding: 12px 24px !important;
            touch-action: manipulation;
          }

          .mobile-notice {
            font-size: 13px !important;
            padding: 10px !important;
            margin-bottom: 12px !important;
          }



          .pin-display-section {
            margin-bottom: 16px !important;
          }

          .alert {
            font-size: 14px !important;
            padding: 12px !important;
          }

          .card-content {
            padding: 16px !important;
          }

          .auth-tabs {
            margin-bottom: 16px !important;
          }

          .auth-tabs button {
            min-height: 44px !important;
            font-size: 14px !important;
          }
        }
      `}</style>

      {/* Version Footer */}
      <div style={{
        padding: '20px',
        textAlign: 'center',
        borderTop: '1px solid rgba(255, 215, 0, 0.1)',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        marginTop: '20px'
      }}>
        <p style={{
          color: '#666',
          fontSize: '13px',
          margin: 0
        }}>
          Aureus Africa {getFullVersion()} • {new Date().getFullYear()} Aureus Alliance Holdings (Pty) Ltd
        </p>
      </div>
    </div>
  );
};

export default UnifiedAuthPageClean;
